import { useEffect, useRef, useState } from 'react';
import { Eye, Send, History, Printer, Download, Mail, MessageCircle, FileText } from 'lucide-react';
import styles from '../DocumentCentre.module.css';
import {
  CHANNEL_LABEL,
  DISPATCH_STATUS,
  HISTORICAL_LABEL,
  PAYMENT_STATUS_COLOR,
  RELEASE_BASIS,
  RESULT,
  formatDate,
  formatMoney,
  formatStamp,
} from '../documentCentre.constants';

/** Type-specific facts (spec §6). Full details are behind View. */
const Details = ({ row }) => {
  const d = row.details || {};
  switch (d.kind) {
    case 'financial':
      return (
        <>
          <span className={styles.amount}>{formatMoney(d.amount)}</span>
          <span className={styles.mutedBlock}>Due {formatDate(d.dueDate)}</span>
          {d.linkedInvoice && <span className={styles.mutedBlock}>Linked to {d.linkedInvoice}</span>}
        </>
      );
    case 'flight':
      return (
        <>
          <span>{d.route || '-'}</span>
          <span className={styles.mutedBlock}>Departs {formatDate(d.departureDate)}</span>
          {d.pnr && <span className={styles.mutedBlock}>PNR {d.pnr}</span>}
        </>
      );
    case 'hotel':
      return (
        <>
          <span>{d.hotel || '-'}</span>
          <span className={styles.mutedBlock}>
            {formatDate(d.checkIn)} to {formatDate(d.checkOut)}
          </span>
        </>
      );
    case 'transfer':
      return (
        <>
          <span>{d.route || '-'}</span>
          <span className={styles.mutedBlock}>{formatDate(d.serviceDate)}</span>
          <span className={styles.mutedBlock}>
            {d.direction || '-'}
            {d.serviceType ? `, ${d.serviceType}` : ''}
          </span>
        </>
      );
    case 'car':
      return (
        <>
          <span>{d.desk || '-'}</span>
          <span className={styles.mutedBlock}>
            {formatDate(d.pickupDate)} to {formatDate(d.dropoffDate)}
          </span>
        </>
      );
    case 'cruise':
      return (
        <>
          <span>{d.ship || '-'}</span>
          <span className={styles.mutedBlock}>Sails {formatDate(d.departureDate)}</span>
        </>
      );
    case 'visa':
      return <span>{d.country || '-'}</span>;
    case 'insurance':
      return (
        <>
          <span>Policy {d.policy || '-'}</span>
          {(d.coverageStart || d.coverageEnd) && (
            <span className={styles.mutedBlock}>
              {formatDate(d.coverageStart)} to {formatDate(d.coverageEnd)}
            </span>
          )}
        </>
      );
    case 'booking':
      return <span>Reads: {d.status}</span>;
    default:
      return <span className={styles.muted}>-</span>;
  }
};

const Release = ({ row }) => {
  const { release } = row;
  if (release.eligible) {
    const basis = release.financial?.basis;
    return (
      <>
        <span className={styles.okText}>Eligible</span>
        {basis && <span className={styles.mutedBlock}>{RELEASE_BASIS[basis] || basis}</span>}
        {release.warnings?.[0] && <span className={styles.warnText}>{release.warnings[0].label}</span>}
      </>
    );
  }
  const more = release.blocks.length - 1;
  return (
    <span title={release.blocks.map((b) => b.label).join('\n')}>
      <span className={styles.statusText} style={{ color: '#dc2626' }}>
        Blocked
      </span>
      <span className={styles.reasonText}>
        {release.blockReason}
        {more > 0 ? ` (+${more} more)` : ''}
      </span>
    </span>
  );
};

const Dispatch = ({ row }) => {
  if (row.historical) return <span className={styles.muted}>-</span>;
  const status = DISPATCH_STATUS[row.dispatch.status] || DISPATCH_STATUS.not_sent;
  const last = row.dispatch.last;
  return (
    <>
      <span className={styles.statusText} style={{ color: status.color }}>
        {status.label}
      </span>
      {last && (
        <span className={styles.mutedBlock}>
          {formatStamp(last.at)}, {CHANNEL_LABEL[last.channel] || last.channel}, {RESULT[last.status]?.label || last.status}
          {last.version !== row.version ? ` (v${last.version})` : ''}
        </span>
      )}
      {row.dispatch.status === 'not_sent' && row.dispatch.previousVersionSent && (
        <span className={styles.warnText}>An earlier version was sent</span>
      )}
    </>
  );
};

const ProcessMenu = ({ row, canSend, onProcess, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [onClose]);
  const pick = (channel) => () => {
    onClose();
    onProcess(row, channel);
  };
  return (
    <div className={styles.menu} ref={ref} role="menu">
      <button type="button" className={styles.menuItem} onClick={pick('print')}>
        <Printer size={14} /> Print
      </button>
      <button type="button" className={styles.menuItem} onClick={pick('download')}>
        <Download size={14} /> Download PDF
      </button>
      <button type="button" className={styles.menuItem} onClick={pick('email')} disabled={!canSend}>
        <Mail size={14} />
        <span>
          Email
          {!canSend && <span className={styles.menuHint}>No permission to send</span>}
        </span>
      </button>
      <button type="button" className={styles.menuItem} onClick={pick('whatsapp')} disabled={!canSend}>
        <MessageCircle size={14} />
        <span>
          WhatsApp
          {!canSend && <span className={styles.menuHint}>No permission to send</span>}
        </span>
      </button>
    </div>
  );
};

const DocumentTable = ({
  rows,
  selected,
  onToggleRow,
  onTogglePage,
  pageState,
  onView,
  onProcess,
  onHistory,
  onOpenBooking,
  canSend,
}) => {
  const [menuFor, setMenuFor] = useState(null);
  const headerRef = useRef(null);

  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = pageState === 'some';
  }, [pageState]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.checkCell}>
            <input
              ref={headerRef}
              type="checkbox"
              className={styles.checkbox}
              checked={pageState === 'all'}
              onChange={onTogglePage}
              aria-label="Select the documents on this page"
            />
          </th>
          <th>Document</th>
          <th>Booking</th>
          <th>Travel date</th>
          <th>Details</th>
          <th>Payment</th>
          <th>Release</th>
          <th>Last dispatch</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isSelected = selected.has(row.id) && !row.historical;
          return (
            <tr
              key={row.rowKey}
              className={`${isSelected ? styles.rowSelected : ''} ${row.historical ? styles.rowHistorical : ''}`}
            >
              <td className={styles.checkCell}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={isSelected}
                  disabled={Boolean(row.historical)}
                  title={row.historical ? 'Historical rows can be viewed, not processed' : undefined}
                  onChange={() => onToggleRow(row)}
                  aria-label={`Select ${row.documentNumber || row.typeLabel}`}
                />
              </td>
              <td>
                <span className={styles.docType}>{row.typeLabel}</span>
                <span className={styles.mutedBlock}>
                  <span className={styles.docNumber}>{row.documentNumber || 'No number'}</span>
                  {row.version > 1 && <span className={styles.versionTag}>v{row.version}</span>}
                  {row.historical && <span className={styles.histTag}>{HISTORICAL_LABEL[row.historical]}</span>}
                </span>
                <span className={styles.mutedBlock}>Issued {formatDate(row.issueDate)}</span>
              </td>
              <td>
                {row.bookingId ? (
                  <button type="button" className={styles.bookingLink} onClick={() => onOpenBooking(row)}>
                    {row.bookingReference}
                  </button>
                ) : (
                  <span>{row.bookingReference}</span>
                )}
                <span className={styles.mutedBlock}>{row.customerName || '-'}</span>
              </td>
              <td>
                <span>{formatDate(row.travelDate)}</span>
                {row.destination && <span className={styles.mutedBlock}>{row.destination}</span>}
              </td>
              <td>
                <Details row={row} />
              </td>
              <td>
                {row.paymentStatus ? (
                  <span className={styles.statusText} style={{ color: PAYMENT_STATUS_COLOR[row.paymentStatus] || '#475569' }}>
                    {row.paymentStatus}
                  </span>
                ) : (
                  <span className={styles.muted}>-</span>
                )}
                {row.outstanding > 0.005 && (
                  <span className={styles.mutedBlock}>Outstanding {formatMoney(row.outstanding)}</span>
                )}
              </td>
              <td>
                <Release row={row} />
              </td>
              <td>
                <Dispatch row={row} />
              </td>
              <td>
                <div className={styles.rowActions}>
                  <button type="button" className={styles.viewBtn} onClick={() => onView(row)} title="View the document">
                    <Eye size={13} /> View
                  </button>
                  {!row.historical && (
                    <button
                      type="button"
                      className={styles.processBtn}
                      onClick={() => setMenuFor(menuFor === row.rowKey ? null : row.rowKey)}
                      title="Print, download or send"
                      aria-haspopup="menu"
                      aria-expanded={menuFor === row.rowKey}
                    >
                      <Send size={13} /> Process
                    </button>
                  )}
                  <button type="button" onClick={() => onHistory(row)} title="Versions and dispatch history">
                    <History size={13} />
                  </button>
                  {menuFor === row.rowKey && (
                    <ProcessMenu row={row} canSend={canSend} onProcess={onProcess} onClose={() => setMenuFor(null)} />
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const EmptyTable = ({ filtered }) => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <FileText size={26} />
    </div>
    <div className={styles.emptyTitle}>No documents found</div>
    <div className={styles.emptyHint}>
      {filtered
        ? 'No document matches these filters. Clear a filter to see more.'
        : 'Documents appear here once they are generated in the booking or by Finance.'}
    </div>
  </div>
);

export default DocumentTable;
